import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch'; 
import * as XLSX from 'xlsx';



// Multi-suggest component for selecting multiple students
function StudentMultiSuggest({ students, selected, onChange }) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filtered = students.filter(
    s => {
      const studentName = s.name || s.student_name || '';
      const studentClass = s.kelas || s.class || s.student_class || '';
      const selectedNames = selected.map(sel => sel.name || sel.student_name || '');
      return studentName.toLowerCase().includes(input.toLowerCase()) && 
             !selectedNames.includes(studentName);
    }
  );

  function addStudent(student) {
    const normalizedStudent = {
      name: student.name || student.student_name,
      kelas: student.kelas || student.class || student.student_class,
      nis: student.nis || student.student_id
    };
    onChange([...selected, normalizedStudent]);
    setInput('');
    setShowSuggestions(false);
  }
  function removeStudent(student) {
    const studentName = student.name || student.student_name;
    onChange(selected.filter(sel => (sel.name || sel.student_name) !== studentName));
  }
  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder="Ketik nama siswa..."
        value={input}
        onChange={e => {
          setInput(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        style={{ width: '100%', fontSize: '1rem', padding: '0.45rem 0.7rem', borderRadius: 8, border: '1px solid #ddd', marginBottom: 8, height: 36 }}
      />
      {showSuggestions && input && filtered.length > 0 && (
        <ul style={{
          position: 'absolute', left: 0, right: 0, top: '100%', background: 'white', zIndex: 10, border: '1px solid #eee', borderRadius: 8, margin: 0, padding: 0, listStyle: 'none', maxHeight: 180, overflowY: 'auto'
        }}>
          {filtered.map((student, idx) => {
            const studentName = student.name || student.student_name || '';
            const studentClass = student.kelas || student.class || student.student_class || '';
            return (
              <li key={studentName + studentClass} style={{ padding: '0.75rem 1rem', cursor: 'pointer', borderBottom: idx !== filtered.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                onMouseDown={() => addStudent(student)}>
                {studentName} <span style={{ color: '#888', fontSize: 13 }}>({studentClass})</span>
              </li>
            );
          })}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {selected.map(student => {
          const studentName = student.name || student.student_name || '';
          const studentClass = student.kelas || student.class || student.student_class || '';
          return (
            <div key={studentName + studentClass} style={{ background: '#f7f7f7', borderRadius: 8, padding: '0.32rem 0.7rem', display: 'flex', alignItems: 'center', fontSize: '1rem', height: 32 }}>
              {studentName} <span style={{ color: '#888', fontSize: '0.95rem', marginLeft: 4 }}>({studentClass})</span>
              <button type="button" aria-label="remove" onClick={() => removeStudent(student)} style={{ background: 'none', border: 'none', marginLeft: 8, fontSize: 18, cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


const AdminLaporanKTSPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tipeFilter, setTipeFilter] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [editedReport, setEditedReport] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: true, // Open by default since we're in a laporan page
    masterDataOpen: false
  });
  const [user, setUser] = useState(null);
  const [ktsReports, setKtsReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);

  // Check admin access
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const userRole = localStorage.getItem('userRole');
    
    if (!isAuthenticated || userRole !== 'admin_user') {
      navigate('/login');
      return;
    }
    
    // If we get here, user is authenticated admin
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        navigate('/login');
      }
    }
  }, [navigate]);

  // Fetch KTS reports from API
  const fetchKtsReports = async (page = 1, search = '', date = '', status = '', tipe = '') => {
    try {
      setLoading(true);
      let url = `/api/admin/kts-reports?page=${page}&per_page=${itemsPerPage}`;
      if (search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (date.trim()) {
        url += `&date=${encodeURIComponent(date.trim())}`;
      }
      if (status.trim()) {
        url += `&status=${encodeURIComponent(status.trim())}`;
      }
      if (tipe.trim()) {
        url += `&tipe=${encodeURIComponent(tipe.trim())}`;
      }
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setKtsReports(data.data || data);
        // Update pagination info if available
        if (data.meta) {
          setTotalPages(data.meta.last_page);
          setTotalItems(data.meta.total);
        }
      } else {
        console.error('Failed to fetch KTS reports');
        setNotification({
          show: true,
          message: 'Gagal memuat data laporan KTS',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching KTS reports:', error);
      setNotification({
        show: true,
        message: 'Terjadi kesalahan saat memuat data',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch students for multi-suggest
  const fetchStudents = async () => {
    try {
      const response = await authFetch('/api/admin/students');
      if (response.ok) {
        const data = await response.json();
        setStudents(data.data || data);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Fetch admin users for decision maker info
  const fetchAdminUsers = async () => {
    try {
      const response = await authFetch('/api/admin_users');
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data.data || data);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      // Set empty array on error to avoid issues
      setAdminUsers([]);
    }
  };

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    
    if (user?.role === 'admin_user' || userRole === 'admin_user') {
      fetchKtsReports(currentPage, searchQuery, dateFilter, statusFilter, tipeFilter);
      fetchStudents();
      fetchAdminUsers();
    }
  }, [user, currentPage, searchQuery, dateFilter, statusFilter, tipeFilter]);

  const handleRowClick = (report) => {
    setSelectedReport(report);
    setEditedReport({ ...report });
  };

  const handleStudentClick = async (studentFromReport) => {
    const studentId = studentFromReport.id || studentFromReport.student_id;
    const studentNis = studentFromReport.nis;

    let studentData = null;

    if (studentId) {
      studentData = students.find(s => s.id === studentId);
    } else if (studentNis) {
      studentData = students.find(s => s.nis === studentNis);
    }
    
    if (!studentData) {
        studentData = students.find(s => s.name === (studentFromReport.name || studentFromReport.student_name));
    }

    if (studentData) {
      // Fetch complete student data with average_points and wali_kelas_name
      try {
        const response = await authFetch(`/api/students/${studentData.id}`);
        if (response.ok) {
          const completeStudentData = await response.json();
          const studentProfile = completeStudentData.data || completeStudentData;
          
          // Merge the complete data with existing data
          const enrichedStudentData = {
            ...studentData,
            average_points: studentProfile.average_points || 0,
            wali_kelas_name: studentProfile.wali_kelas_name || studentProfile.homeroom_teacher || null
          };
          
          navigate('/admin/status-siswa', { state: { student: enrichedStudentData } });
        } else {
          // Fallback to original data if API call fails
          navigate('/admin/status-siswa', { state: { student: studentData } });
        }
      } catch (error) {
        console.error('Error fetching complete student data:', error);
        // Fallback to original data if API call fails
        navigate('/admin/status-siswa', { state: { student: studentData } });
      }
    } else {
      if (studentId) {
          navigate('/admin/status-siswa', { state: { student: studentFromReport } });
      } else {
        setNotification({
          show: true,
          message: `Tidak dapat menemukan detail untuk siswa: ${studentFromReport.name || studentFromReport.student_name}`,
          type: 'error',
        });
      }
    }
  };

  // Use ktsReports directly since filtering is now handled server-side
  const currentPageItems = ktsReports;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Reset to first page when search query changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const handleApproval = async (reportId, action) => {
    try {
      console.log('Updating KTS report:', reportId, 'action:', action);
      
      // First, save all the form changes
      if (editedReport) {
        console.log('Saving form changes before status update');
        // Prepare update data, only including valid fields
        const updateData = {
          date: editedReport.date,
          students: editedReport.students || [],
          tipe: editedReport.tipe,
          category: editedReport.category,
          total_hour: editedReport.total_hour === '' ? null : editedReport.total_hour,
          total_hour_kersos: editedReport.total_hour_kersos === '' ? null : editedReport.total_hour_kersos,
          appreciation_point: editedReport.appreciation_point === '' ? null : editedReport.appreciation_point,
          additional_consequence: editedReport.additional_consequence,
          ketidaksesuaian: editedReport.ketidaksesuaian,
          chronology: editedReport.chronology,
        };

        // Only include section if it's valid and not empty (not required for ketidakhadiran)
        if (editedReport.section && editedReport.section.trim() !== '') {
          updateData.section = editedReport.section;
        }

        // Only include teacher_name if the report was created by a teacher (has teacher_id)
        if (editedReport.teacher_id && (editedReport.teacher_name || editedReport.teacherName)) {
          updateData.teacher_name = editedReport.teacher_name || editedReport.teacherName;
        }

        // Only include absent_type if it's valid and category is ketidakhadiran
        if (editedReport.category === 'ketidakhadiran' && editedReport.absent_type && editedReport.absent_type.trim() !== '') {
          updateData.absent_type = editedReport.absent_type;
        }

        // Only include shift if it's valid and not empty
        if (editedReport.shift) {
          updateData.shift = editedReport.shift;
        }

        const isPraktekKetidakhadiran =
          (editedReport?.tipe || 'praktek') === 'praktek' &&
          (editedReport?.category || 'kompensasi') === 'ketidakhadiran';

        if (isPraktekKetidakhadiran) {
            updateData.total_hour = editedReport.total_hour === '' ? 0 : editedReport.total_hour;
            updateData.total_hour_kersos = editedReport.total_hour_kersos === '' ? 0 : editedReport.total_hour_kersos;
            updateData.appreciation_point = editedReport.appreciation_point === '' ? 0 : editedReport.appreciation_point;
        }

        console.log('Sending update data:', updateData);

        const updateResponse = await authFetch(`/api/admin/kts-reports/${reportId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });

        if (!updateResponse.ok) {
          throw new Error('Failed to save form changes');
        }
      }
      
      // Then, update the status
      console.log('Updating status to:', action === 'approve' ? 'approved' : 'rejected');
      const statusResponse = await authFetch(`/api/admin/kts-reports/${reportId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: action === 'approve' ? 'approved' : 'rejected'
        }),
      });
      
      if (statusResponse.ok) {
        const result = await statusResponse.json();
        console.log('Update result:', result);
        
        setNotification({
          show: true,
          message: `Laporan berhasil ${action === 'approve' ? 'disetujui' : 'ditolak'}`,
          type: 'success'
        });
        fetchKtsReports(currentPage, searchQuery, dateFilter, statusFilter, tipeFilter); // Refresh the data with current filters
        setSelectedReport(null);
        setEditedReport(null);
      } else {
        let errorData;
        try {
          errorData = await statusResponse.json();
        } catch (e) {
          errorData = await statusResponse.text();
        }
        console.error('Error response:', errorData);
        throw new Error(`Failed to update status: ${statusResponse.status} ${statusResponse.statusText}`);
      }
    } catch (error) {
      console.error('Error updating KTS report:', error);
      setNotification({
        show: true,
        message: `Gagal mengupdate laporan: ${error.message}`,
        type: 'error'
      });
    }
  };

  // When a report is selected, initialize editedReport
  React.useEffect(() => {
    if (selectedReport) {
      setEditedReport({ ...selectedReport });
    } else {
      setEditedReport(null);
    }
  }, [selectedReport]);

  // Date formatting helper function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch (error) {
      return '-';
    }
  };

  // Helper function to get admin name by decision_by_id
  const getDecisionMakerName = (report) => {
    if (!report) {
      return null;
    }

    // Ideal case: The backend sends the related decision_by object with the name.
    if (report.decision_by && report.decision_by.name) {
      return report.decision_by.name;
    }

    // Fallback: If only decision_by_id is available, look up the name in adminUsers list.
    if (report.decision_by_id && adminUsers.length > 0) {
      // Convert both IDs to numbers for comparison to avoid type issues
      const decisionByIdNum = parseInt(report.decision_by_id);
      const admin = adminUsers.find(admin => parseInt(admin.id) === decisionByIdNum);
      return admin ? admin.name : null;
    }

    return null;
  };

  // Helper function to get reporter name based on admin_user_id or teacher_id
  const getReporterName = (report) => {
    // If admin_user exists, use admin user name
    if (report.admin_user) {
      return report.admin_user.name;
    }
    
    // If admin_user_id exists but no admin_user object, fallback to adminUsers array
    if (report.admin_user_id && adminUsers.length > 0) {
      const admin = adminUsers.find(admin => admin.id == report.admin_user_id);
      if (admin) {
        return admin.name;
      }
    }
    
    // Otherwise, use teacher name if teacher_id exists
    if (report.teacher_id && (report.teacher_name || report.teacherName)) {
      return report.teacher_name || report.teacherName;
    }
    
    return 'Unknown';
  };

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Excel export function
  const handleExcelExport = async () => {
    try {
      setNotification({
        show: true,
        message: 'Memproses ekspor Excel...',
        type: 'success'
      });

      // Check if user is authenticated before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      // Build query parameters based on current filters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (dateFilter) params.append('date', dateFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (tipeFilter) params.append('tipe', tipeFilter);

      console.log('Making Excel export request to:', `/api/admin/kts-reports/export-excel?${params.toString()}`);
      console.log('Authentication token present:', !!token);

      const response = await authFetch(`/api/admin/kts-reports/export-excel?${params.toString()}`, {
        method: 'PUT',
        autoLogoutOn401: false
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Get more detailed error information
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.text();
          console.log('Error response body:', errorData);
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (e) {
          console.log('Could not read error response body');
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Export data received:', data);
      
      // Validate data structure
      if (!data || !data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid data format received from server');
      }

      if (data.data.length === 0) {
        setNotification({
          show: true,
          message: 'Tidak ada data untuk diekspor dengan filter yang dipilih',
          type: 'error'
        });
        return;
      }
      
      // Create Excel workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data.data);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Laporan KTS');
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Laporan_KTS_${currentDate}.xlsx`;
      
      // Download the file
      XLSX.writeFile(workbook, filename);
      
      setNotification({
        show: true,
        message: `File Excel berhasil diunduh: ${filename} (${data.data.length} records)`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Gagal mengekspor data ke Excel';
      if (error.message.includes('No authentication token')) {
        errorMessage = 'Sesi telah berakhir. Silakan login kembali.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Tidak memiliki akses. Silakan login kembali.';
      } else if (error.message.includes('403')) {
        errorMessage = 'Tidak memiliki izin untuk mengekspor data.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Endpoint ekspor tidak ditemukan. Hubungi administrator.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Terjadi kesalahan server. Coba lagi nanti.';
      } else if (error.message.includes('timeout') || error.message.includes('network')) {
        errorMessage = 'Koneksi timeout. Periksa koneksi internet Anda.';
      }
      
      setNotification({
        show: true,
        message: `${errorMessage} (Detail: ${error.message})`,
        type: 'error'
      });
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Laporan Ketidaksesuaian (KTS)</h1>
            <Subtitle>Kelola dan tinjau laporan ketidaksesuaian</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <FiltersContainer>
                <SearchContainer>
                  <SearchIcon>🔍</SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Cari berdasarkan nama guru, siswa, atau deskripsi..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchContainer>
                <FilterGroup>
                  <FilterInput
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    placeholder="Filter tanggal"
                  />
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Semua Status</option>
                    <option value="Menunggu">Menunggu</option>
                    <option value="Disetujui">Disetujui</option>
                    <option value="Ditolak">Ditolak</option>
                  </FilterSelect>
                  <FilterSelect
                    value={tipeFilter}
                    onChange={(e) => setTipeFilter(e.target.value)}
                  >
                    <option value="">Semua Tipe</option>
                    <option value="praktek">Praktek</option>
                    <option value="teori">Teori</option>
                  </FilterSelect>
                  <ExportButton onClick={handleExcelExport}>
              📊 Export Excel
            </ExportButton>
                </FilterGroup>
              </FiltersContainer>
            </ToolbarSection>

            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal</th>
                    <th>Pelapor</th>
                    <th>Siswa</th>
                    <th>Jenis</th>
                    <th>Deskripsi</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {currentPageItems.length > 0 ? (
                    currentPageItems.map((report) => (
                      <TableRow
                        key={report.id}
                        onClick={() => handleRowClick(report)}
                        selected={selectedReport?.id === report.id}
                      >
                        <td>{formatDate(report.date)}</td>
                        <td>{getReporterName(report)}</td>
                        <td>
                          {Array.isArray(report.students) && report.students.length > 0
                            ? report.students.map((student, index) => (
                                <React.Fragment key={student.id || student.nis || index}>
                                  <StudentLink onClick={(e) => {
                                    e.stopPropagation();
                                    handleStudentClick(student);
                                  }}>
                                    {student.name || student.student_name}
                                  </StudentLink>
                                  {index < report.students.length - 1 ? ', ' : ''}
                                </React.Fragment>
                              ))
                            : (report.student_name || '')}
                        </td>
                        <td>{report.ketidaksesuaian}</td>
                        <td>{report.category}</td>
                        <td>
                          <StatusBadge $status={report.status}>
                            {report.status === 'pending' ? 'Menunggu' : report.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => { e.stopPropagation(); handleRowClick(report); }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>
                        Tidak ada laporan
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            <PaginationContainer>
              <PaginationInfo>
                Menampilkan {startIndex + 1}-{Math.min(endIndex, totalItems)} dari {totalItems} laporan
              </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  <PageNumbers>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <PageNumber
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          $active={currentPage === pageNum}
                        >
                          {pageNum}
                        </PageNumber>
                      );
                    })}
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
            ×
          </CloseNotificationButton>
        </NotificationToast>
      )}

      {selectedReport && (
        <DetailModal onClick={() => setSelectedReport(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <DetailHeader>
              <DetailHeaderContent>
                <h2>Edit Laporan Ketidaksesuaian</h2>
                <StatusContainer>
                  <DetailStatus $status={selectedReport.status}>
                    {selectedReport.status === 'pending' ? 'Menunggu' :
                      selectedReport.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                  </DetailStatus>
                  {selectedReport.status !== 'pending' && (
                    <DecisionMaker>
                      Diputuskan oleh: {getDecisionMakerName(selectedReport) || 'Admin'}
                    </DecisionMaker>
                  )}
                </StatusContainer>
              </DetailHeaderContent>
              <CloseButton onClick={() => setSelectedReport(null)}>×</CloseButton>
            </DetailHeader>

            <div>
              <DetailContent>
                {/* NAMA SISWA FIELD WITH MULTI-SUGGESTION */}
                <DetailSection>
                  <SectionTitle>Nama Siswa</SectionTitle>
                  <div style={{ marginBottom: '1rem' }}>
                    <StudentMultiSuggest
                      students={students}
                      selected={
                        (editedReport?.students && editedReport.students.length > 0)
                          ? editedReport.students
                          : (selectedReport?.students || selectedReport?.studentInfo || [])
                      }
                      onChange={students => setEditedReport({ ...editedReport, students })}
                    />
                  </div>
                </DetailSection>
                <DetailSection>
                  <SectionTitle>Informasi Umum</SectionTitle>
                  <DetailGrid>
                    <DetailGroup>
                      <DetailLabel>Tanggal Laporan</DetailLabel>
                      <FormInput 
                        type="date" 
                        value={editedReport?.date || selectedReport?.date || selectedReport?.report_date || ''} 
                        onChange={e => setEditedReport({ ...editedReport, date: e.target.value })} 
                      />
                    </DetailGroup>
                    <DetailGroup>
                      <DetailLabel>Pelapor</DetailLabel>
                      <FormInput 
                        type="text" 
                        value={getReporterName(editedReport || selectedReport)} 
                        onChange={e => setEditedReport({ ...editedReport, teacher_name: e.target.value })} 
                      />
                    </DetailGroup>
                    {/* TIPE FIELD */}
                    <DetailGroup>
                      <DetailLabel>Tipe</DetailLabel>
                      <FormSelect 
                        value={editedReport?.tipe || selectedReport?.tipe || selectedReport?.class_type || 'praktek'} 
                        onChange={e => setEditedReport({ ...editedReport, tipe: e.target.value })}
                      >
                        <option value="praktek">Praktek</option>
                        <option value="teori">Teori</option>
                       
                      </FormSelect>
                    </DetailGroup>
                    {/* KATEGORI FIELD */}
                    <DetailGroup>
                      <DetailLabel>Kategori</DetailLabel>
                      <FormSelect 
                        value={editedReport?.category || selectedReport?.category || 'kompensasi'} 
                        onChange={e => setEditedReport({ ...editedReport, category: e.target.value })}
                      >
                        <option value="kompensasi">Kompensasi</option>
                        <option value="ketidakhadiran">Ketidakhadiran</option>
                      </FormSelect>
                    </DetailGroup>

                    {/* TIPE KETIDAKHADIRAN FIELD - Only show when category is ketidakhadiran */}
                    {((editedReport?.category || selectedReport?.category) === 'ketidakhadiran') && (
                      <DetailGroup>
                        <DetailLabel>Tipe Ketidakhadiran</DetailLabel>
                        <FormSelect 
                          value={editedReport?.absent_type || selectedReport?.absent_type || 'sakit'} 
                          onChange={e => setEditedReport({ ...editedReport, absent_type: e.target.value })}
                        >
                          <option value="sakit">Sakit</option>
                          <option value="ijin">Ijin</option>
                          <option value="alfa">Alfa</option>
                          <option value="keterlambatan">Keterlambatan</option>
                          <option value="interupsi">Interupsi</option>
                        </FormSelect>
                      </DetailGroup>
                    )}

                    {/* SECTION */}
                    <DetailGroup>
                      <DetailLabel>Section</DetailLabel>
                      <FormInput 
                        type="text" 
                        value={editedReport?.section || selectedReport?.section || ''} 
                        onChange={e => setEditedReport({ ...editedReport, section: e.target.value })} 
                      />
                    </DetailGroup>
                    {/* SHIFT */}
                    <DetailGroup>
                      <DetailLabel>Shift</DetailLabel>
                      <FormSelect 
                        value={editedReport?.shift || selectedReport?.shift || ''} 
                        onChange={e => setEditedReport({ ...editedReport, shift: e.target.value })}
                      >
                        <option value="">-</option>
                        <option value="Shift -">Shift -</option>
                        <option value="Shift 1">Shift 1 (07:00 - 14:30)</option>
                        <option value="Shift 2">Shift 2 (14:30 - 22:00)</option>
                        
                      </FormSelect>
                    </DetailGroup>

                    <DetailGroup full>
                      <DetailLabel>Ketidaksesuaian</DetailLabel>
                      <FormInput 
                        type="text" 
                        value={editedReport?.ketidaksesuaian || selectedReport?.ketidaksesuaian || ''} 
                        onChange={e => setEditedReport({ ...editedReport, ketidaksesuaian: e.target.value })} 
                      />
                    </DetailGroup>

                    <DetailGroup>
                      <DetailLabel>
                        {((editedReport?.tipe || selectedReport?.tipe || selectedReport?.class_type) === 'teori') 
                          ? 'Jam Lembur' 
                          : 'Jam Kompensasi'
                        }
                      </DetailLabel>
                      <FormInput 
                        type="number" 
                        value={editedReport?.total_hour || selectedReport?.total_hour || ''} 
                        onChange={e => setEditedReport({ ...editedReport, total_hour: e.target.value })} 
                        min="-100"
                        step="0.1"
                      />
                    </DetailGroup>
                    <DetailGroup>
                      <DetailLabel>Jam Kersos</DetailLabel>
                      <FormInput 
                        type="number" 
                        value={editedReport?.total_hour_kersos || selectedReport?.total_hour_kersos || ''} 
                        onChange={e => setEditedReport({ ...editedReport, total_hour_kersos: e.target.value })} 
                        min={((editedReport?.tipe || selectedReport?.tipe) === 'praktek' && (editedReport?.category || selectedReport?.category) === 'ketidakhadiran') ? "-100" : "0"}
                        step="0.1"
                      />
                    </DetailGroup>
                    <DetailGroup>
                      <DetailLabel>Poin Sikap</DetailLabel>
                      <FormInput 
                        type="number" 
                        value={editedReport?.appreciation_point || selectedReport?.appreciation_point || ''} 
                        onChange={e => setEditedReport({ ...editedReport, appreciation_point: e.target.value })} 
                        min={((editedReport?.tipe || selectedReport?.tipe) === 'praktek' && (editedReport?.category || selectedReport?.category) === 'ketidakhadiran') ? "-100" : "0"}
                        step="0.1"
                      />
                    </DetailGroup>
                    <DetailGroup full>
                    <DetailLabel>Konsekuensi Tambahan</DetailLabel>
                    <FormTextarea 
                      value={editedReport?.additional_consequence || selectedReport?.additional_consequence || ''} 
                      onChange={e => setEditedReport({ ...editedReport, additional_consequence: e.target.value })}
                      style={{ minHeight: '60px', resize: 'vertical' }}
                      
                    />
                  </DetailGroup>
                   
                    

                  </DetailGrid>
                </DetailSection>

                <DetailSection>
                  <SectionTitle>Keterangan</SectionTitle>
                  <DetailGroup full>
                    <DetailLabel>Kronologi</DetailLabel>
                    <FormTextarea 
                      value={editedReport?.chronology || selectedReport?.chronology || ''} 
                      onChange={e => setEditedReport({ ...editedReport, chronology: e.target.value })}
                      style={{ minHeight: '80px', resize: 'vertical' }}
                    />
                  </DetailGroup>
                

                </DetailSection>

              </DetailContent>
              <ActionButtons>
                <RejectButton type="button" onClick={() => handleApproval(selectedReport?.id, 'reject')}>
                  Tolak
                </RejectButton>
                <ApproveButton type="button" onClick={() => handleApproval(selectedReport?.id, 'approve')} style={{ backgroundColor: 'var(--success, #2ec27e)' }}>
                  Setujui
                </ApproveButton>
              </ActionButtons>
            </div>
          </DetailCard>
        </DetailModal>
      )}
    </PageContainer>
  );
}

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  // max-width: 500px;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  flex: 1;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.$status || props.status) {
      case 'pending': return '#fff3e0';
      case 'approved': return '#e6f8f5';
      case 'rejected': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.$status || props.status) {
      case 'pending': return '#e67700';
      case 'approved': return '#2ec27e';
      case 'rejected': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StudentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StudentItem = styled.div`
  background-color: var(--background-light);
  padding: 0.75rem;
  border-radius: 8px;
`;

const StudentName = styled.div`
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.25rem;
`;

const StudentInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
  margin-bottom: 0.5rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const DetailValue = styled.div`
  font-size: 0.938rem;
  color: var(--text-dark);
  line-height: 1.5;
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  background-color: ${props => props.$type === 'success' ? '#e6f8f5' : '#ffe6e6'};
  color: ${props => props.$type === 'success' ? '#2ec27e' : '#c01c28'};
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  min-width: 300px;
`;

const NotificationMessage = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: inherit;
  margin-left: 1rem;
`;

const StudentLink = styled.span`
  color: var(--primary, #3b82f6);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--primary-dark, #2563eb);
    text-decoration: underline;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background-light);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-dark);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--background-light);
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: ${props => props.$active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.$active ? 'white' : 'var(--text-dark)'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover {
    background-color: ${props => props.$active ? 'var(--primary)' : 'var(--background-light)'};
    border-color: var(--primary);
  }
`;

const ExportButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  justify-content: center;

  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default AdminLaporanKTSPage;